import { useState } from 'react';
import type { FormEvent } from 'react';
import { SITE, FORM_URL } from '@/constants';

const projectTypes = [
  'Proyecto arquitectónico',
  'Diseño interior',
  'Fachada',
  'Remodelación',
  'Construcción',
  'Render',
  'Curso',
  'Otro',
];

const inputClass =
  'w-full bg-transparent border border-neutral-300 px-4 py-3.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition-colors';

const labelClass = 'overline text-neutral-400 block mb-2';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    setStatus('sending');
    setFormError('');

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const code = parsed?.code;
      if (res.ok && code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        const serverMsg =
          (parsed?.meta as { message?: string } | undefined)?.message ||
          (parsed?.meta as { detail?: string } | undefined)?.detail ||
          (parsed as { message?: string }).message;
        setFormError(serverMsg || 'No se pudo enviar el mensaje. Inténtalo de nuevo.');
      }
    } catch {
      setStatus('error');
      setFormError('Error de conexión. Verifica tu internet e inténtalo de nuevo.');
    }
  };

  return (
    <section id="contacto" className="bg-white text-neutral-900 scroll-mt-20">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
          <div className="lg:col-span-5">
            <p className="overline text-neutral-400 reveal">Contacto</p>
            <h2 className="font-display font-medium tracking-tightest text-[clamp(2rem,5vw,4.5rem)] mt-4 reveal">
              Cuéntanos sobre tu proyecto
            </h2>
            <p className="text-neutral-600 mt-6 leading-relaxed reveal">
              Cuéntanos qué tienes en mente y te respondemos para platicar los
              siguientes pasos.
            </p>
            <div className="mt-10 space-y-4 text-sm reveal">
              <a href={SITE.whatsappLink} target="_blank" rel="nofollow noopener noreferrer" className="flex items-center gap-3 u-link w-fit">
                <i className="ri-whatsapp-line text-lg"></i>
                {SITE.phoneDisplay}
              </a>
              <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 u-link w-fit">
                <i className="ri-mail-line text-lg"></i>
                {SITE.email}
              </a>
              <p className="flex items-center gap-3">
                <i className="ri-map-pin-line text-lg"></i>
                {SITE.location}
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form
              id="contacto-form"
              data-readdy-form
              onSubmit={handleSubmit}
              noValidate={false}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="nombre" className={labelClass}>
                    Nombre
                  </label>
                  <input id="nombre" name="nombre" type="text" required className={inputClass} placeholder="Tu nombre" />
                </div>
                <div>
                  <label htmlFor="telefono" className={labelClass}>
                    Teléfono
                  </label>
                  <input id="telefono" name="telefono" type="tel" className={inputClass} placeholder="Tu teléfono" />
                </div>
                <div>
                  <label htmlFor="email" className={labelClass}>
                    Correo
                  </label>
                  <input id="email" name="email" type="email" required className={inputClass} placeholder="tucorreo@ejemplo.com" />
                </div>
                <div>
                  <label htmlFor="ciudad" className={labelClass}>
                    Ciudad
                  </label>
                  <input id="ciudad" name="ciudad" type="text" className={inputClass} placeholder="Ciudad" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="tipo" className={labelClass}>
                    Tipo de proyecto
                  </label>
                  <select id="tipo" name="tipo" required className={inputClass}>
                    <option value="">Selecciona una opción</option>
                    {projectTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="mensaje" className={labelClass}>
                    Mensaje
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    rows={5}
                    maxLength={500}
                    className={`${inputClass} resize-none`}
                    placeholder="Cuéntanos más sobre tu proyecto..."
                  ></textarea>
                  <p className="text-xs text-neutral-400 mt-2 text-right">Máximo 500 caracteres</p>
                </div>
              </div>

              {/* Campo antispam */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-offset"
              />

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-8 py-4 text-xs font-medium uppercase tracking-[0.18em] bg-neutral-900 text-white hover:bg-neutral-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? 'Enviando...' : 'Enviar mensaje'}
                </button>
              </div>

              {status === 'success' && (
                <p className="mt-6 text-sm text-neutral-700 border border-neutral-200 bg-neutral-50 px-4 py-3">
                  Gracias por tu mensaje. Te contactaremos pronto.
                </p>
              )}
              {status === 'error' && (
                <p className="mt-6 text-sm text-neutral-700 border border-neutral-200 bg-neutral-50 px-4 py-3">
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}